/**
 * @module app.pp
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { NavigationInstruction, RouteConfig } from 'aurelia-router';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { EssAccordionCustomAttribute } from 'app/shared/attributes/ess-collapse-attributes';
import { apiCall } from 'app/shared/lib/essette-api';
import { deepClone } from 'app/shared/lib/utilities';
import { AuthCreatedCustomEvent, AuthExternalSystemChangeEvent, AuthAutoDeterminationEvent, IAuthChangeCustomEventDetail, AuthIQCDocumentSkipEvent } from 'app/um/lib/events';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { WizardNavigator } from 'app/core/components/wizard/wizard-navigator';
import { AuthService } from 'app/um/services/auth-service';
import { getReference } from 'app/shared/models/essette-types';
import { IPopup } from 'app/shared/elements/popup-base';
import { validate } from 'app/shared/elements/ess-rule';
import { guid } from 'app/shared/lib/guid';
import { AuthPortalPageContext } from 'app/pp/lib/auth-portal-page-context';
import { IAssessmentsConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class CreatePage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected auth: UM.Auth = {};

    protected authCreated: boolean = false;
    protected memberPlanId: string | null = null;

    protected showAutoAuth: boolean = false;
    protected showIQCDocumentation: boolean = false;
    protected enableAutoAuth: boolean = false;
    protected isAutoAuthComplete: boolean = false;
    protected hasPortalAssessments: boolean = false;

    protected hasPageAccess: boolean = false;

    protected loaded: Promise<void>;
    protected load!: () => void;

    protected latestStepIndex: number = 0;

    protected statuses: UM.AuthStatus[] = [];
    protected selectedStatus: UM.AuthStatus | null = null;
    protected selectedStatusReason: UM.AuthStatusReason | null = null;

    protected validateForm = validate;
    protected $isValid: boolean = true;

    protected listItemIndex: number = 0;

    private subscriptions: Array<Subscription> = [];

    protected assessmentsConfig: IAssessmentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM'
    };

    protected tasksConfig: ITasksConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth'
    };

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $collapsible!: EssAccordionCustomAttribute;
    protected $steps: IWizardStep[] = [];
    protected $wizard!: WizardNavigator;
    protected $modal!: IPopup;
    protected accordionElement!: HTMLUListElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly context: AuthPortalPageContext,
        private readonly authService: AuthService,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;

        this.loaded = new Promise<void>((resolve) => this.load = resolve);
    }

    protected async canActivate({ authId, memberPlanId }: { authId: string, memberPlanId?: string }): Promise<boolean> {
        await this.checkPermissions();
        await this.loadAuthFromContext(authId, memberPlanId || null);

        return this.hasPageAccess;
    }

    protected async activate({ authId, memberPlanId }: { authId: string, memberPlanId: string }, routeConfig: RouteConfig, navInstruction: NavigationInstruction): Promise<void> {
        await this.checkForPlugins();
        this.hasPortalAssessments = !!this.auth.assessments?.find(assessment => assessment?.attachment?.createdInPortal);

        this.subscriptions.push(
            this.events.subscribe(AuthAutoDeterminationEvent, (e: AuthAutoDeterminationEvent) => this.reload({ authReloaded: false, triggerRules: true }))
        );
        if(this.showAutoAuth) {
            //here is the event which CWQI-Manager will actually raise.
            // not CwqiChangedEvent, not CwqiSavedEvent.
            //CWQI is the only process that does this in Portal; IQC (for instance) is full UM only.
            this.subscriptions.push(
                this.events.subscribe(AuthExternalSystemChangeEvent, () => { this.$wizard.continueToNextSection() })
            );
        }
        if(this.showIQCDocumentation) {
            this.subscriptions.push(
                this.events.subscribe(AuthIQCDocumentSkipEvent, () => { this.$wizard.skipToNextSection() })
            );
        }
    }

    protected attached(): void {
        this.load();
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected authCreatedEvent(event: AuthCreatedCustomEvent) {
        if(!!event?.detail?.authId) {
            this.authCreationComplete(event.detail.authId, true);
        }
    }

    protected async authCreationComplete(authId: string, reload: boolean): Promise<void> {
        this.authCreated = true;
        if(reload) await this.loadAuthFromContext(authId, null);
        this.hasPortalAssessments = !!this.auth.assessments?.find(assessment => assessment?.attachment?.createdInPortal);
        //when we have an auth id, navigate without reloading to that url so it changes and
        //clicking submit request will trigger a reload
        this.router.navigate(`#/auths/create?authId=${authId}`, { trigger: false });
    }


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasPageAccess = this.accountContext.hasRouteAccess('pp.auths.create');
    }

    protected incrementStepCounter(): number {
        return ++this.listItemIndex;
    }

    protected isSectionEnabled(item: Element): boolean {
        if(!this.auth.id) return false;
        else if(!this.auth.isPending) return true;

        return this.$wizard?.isSectionEnabled(item);
    }

    //this will be called again after step 2 is completed
    public checkAutoAuthIsNeeded() {
        this.loadAuthFromContext(this.auth?.id || '', this.memberPlanId || '');
        this.enableAutoAuth = this.auth?.diagnoses?.length !== 0 || this.auth.services?.length !== 0 ? true : false;
    }

    private async checkForPlugins() {
        if(!this.context.apiSettingsSet) {
            await this.context.loadBasicSettings();
        }
        this.showAutoAuth = this.context.portalAutoAuth ?? false;
        this.showIQCDocumentation = this.context.portalIQC ?? false;
    }

    public async reload({ authReloaded, triggerRules }: IAuthChangeCustomEventDetail): Promise<void> {
        if(!authReloaded && this.auth?.id) {
            await this.context.refreshAuthSettings(this.auth);
        }

        // Keep wizard step bindings in sync with the latest context auth
        this.auth = deepClone(this.context.auth);

        if(triggerRules) {
            this.context.triggerCorrespondenceRules();
        }
    }

    protected onCloseSection(stepIndex: number): void {
        if(stepIndex !== this.latestStepIndex) {
            this.$wizard.closeStep(stepIndex);
        }
        this.accordionElement.children[stepIndex]?.querySelector('div:first-child')?.setAttribute("aria-expanded", "false");
    }

    protected onOpenSection(stepIndex: number): void {
        this.accordionElement.children[stepIndex]?.querySelector('div:first-child')?.setAttribute("aria-expanded", "true");
    }

    protected async loadAuthFromContext(authId: string, memberPlanId: string | null): Promise<void> {
        if(authId && this.auth?.id !== authId) {
            await this.configureContext(authId);

            this.auth = this.context.auth;
            //If we are not a portal auth, clear the auth, so they can't see it
            if(!this.auth.isPending && this.auth.source?.code !== "PORTAL") {
                this.auth = {};
                this.authCreated = false;
                this.loaded.then(() => this.$collapsible.open(0));
                throw new Error("Auth is not a portal auth.");
            }
            this.enableAutoAuth = this.auth?.diagnoses?.length !== 0 || this.auth.services?.length !== 0 ? true : false;
            this.authCreated = true;

            this.loaded.then(() => {
                if(this.$wizard && this.auth.isPending) {
                    this.$wizard.continueToNextSection();
                } else {
                    this.$collapsible.open(this.$steps.length - 1);
                }
            });
        } else {
            this.auth = {};
            this.authCreated = false;
            this.loaded.then(() => this.$collapsible.open(0));
        }

        if(memberPlanId) {
            this.memberPlanId = memberPlanId;
            if(this.auth && this.auth.member?.plan?.id !== this.memberPlanId) {
                throw new Error('Auth selected and member plan selected do not match.');
            }
        }
    }

    protected openCancelModal() {
        this.loadLookups();
        this.selectedStatus = null;
        this.selectedStatusReason = null;
        this.$modal.open();
    }

    protected constructSaveObject(): UM.Auth {
        if(!this.auth || !this.auth.id) { return {}; }

        if(!this.selectedStatus || !this.selectedStatus.id) { return {}; }

        const saveAuth: UM.Auth = {
            id: this.auth.id,
            removeFromPortalQueue: true,
            status: getReference(this.selectedStatus) || undefined
        }

        if(this.selectedStatusReason && saveAuth.status) {
            saveAuth.status.reason = getReference(this.selectedStatusReason) || undefined;
        }

        return saveAuth;
    }

    public async submit(): Promise<boolean> {
        if(!await this.cancelAuth())
            return false;

        this.$modal.close();
        this.formSuccess();
        return true;
    }

    protected close() {
        this.selectedStatus = null;
        this.selectedStatusReason = null;
        this.$modal.close();
    }

    protected formSuccess() {
        this.memberPlanId = null;
        this.auth = {};
        this.router.navigate(`#/auths/create`);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async configureContext(authId: string): Promise<void> {
        try {
            this.state.beginLoad();
            await this.context.configure(authId);

        } catch(error) {
            this.logger.error('Error loading auth', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadLookups() {
        const includes: string[] = [
            'reasons'
        ];

        try {
            this.state.beginLoad();
            const response = await this.authService.loadVoidedAuthStatuses({ all: true }, includes);
            this.statuses = response.results;

            if(this.statuses.length === 1) {
                this.selectedStatus = this.statuses[0];

                if(this.selectedStatus.reasons?.length === 1) {
                    this.selectedStatusReason = this.selectedStatus.reasons[0];
                }
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async cancelAuth(): Promise<boolean> {
        const saveAuth = this.constructSaveObject();

        try {
            this.state.beginLoad();
            await this.authService.saveAuthStatus(saveAuth);
            this.auth.isPending = false;
            if(this.auth.id) await this.context.configure(this.auth.id); // reconfigure context
        } catch(error) {
            this.logger.error('Error updating auth', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
        return true;
    }

}
